/**
 * 0.36.714 — {element_id} interpolation for display-only element
 * text (title, hint, markdown).
 * 0.36.715 — Doubled braces are escapes: {{key}} renders as
 * literal {key}, {{ renders as {, }} renders as }. Standard
 * mustache-style escaping.
 * 0.38.23 — Optional formatValue callback lets callers apply the
 * number-format cascade to substituted values. Without it, values
 * fall through String() as before. Called with the raw value; must
 * return the string to substitute. Recommended shape: a closure
 * bound to the ctx that resolveAdminText already has, so the
 * caller keeps ownership of cascade logic and this helper stays
 * agnostic about form state.
 *
 * Substitutes each {key} occurrence with String(values[key] ?? '')
 * (or, when formatValue is given, whatever formatValue returns):
 *
 *   - Missing / undefined values render as empty string.
 *   - Numbers coerce naturally (or format via callback).
 *   - Booleans become "true" / "false".
 *   - Arrays render as "[object Object],..." for row arrays.
 *     Admin should point at derived count / boolean keys.
 *
 * Placeholder keys accept letters, digits, underscores, dashes.
 * Anything else (spaces, punctuation) doesn't match; text passes
 * through untouched.
 */

import { readValue, listText } from '../lib/readValue';

// 0.45.89 - Dots too: `{sign.numberAlias}` reads into a flow variable
// holding a whole row.
// 0.46.16 - And brackets after a name: `{sign.nature.goods[].name.en}`
// (every item of a list, joined by commas) or `{sign.details[0].text}`.
const PLACEHOLDER = /\{([A-Za-z0-9_-]+(?:\[\d*\])*(?:\.[A-Za-z0-9_-]+(?:\[\d*\])*)*)\}/g;

// 0.36.715 — Sentinel tokens used for the escape pass. Contain
// characters that can't appear in a user-typed placeholder key, so
// they can't collide with real content.
const LBRACE_SENTINEL = '\u0000LB\u0000';
const RBRACE_SENTINEL = '\u0000RB\u0000';

export function interpolateValues(text, values, formatValue) {
  // 0.38.61 - Coerce non-string input to a string BEFORE any other
  // processing. Previously returned the non-string input unchanged,
  // which triggered React error #31 downstream when a caller rendered
  // the result as a JSX child. Bad-data cases we've seen: an
  // element's e.text / e.help / e.caption arriving as a plain object
  // (an unparsed translation map, or a values-map bleed from a data
  // migration). String() gives "[object Object]" for objects, "null"
  // for null, "12" for numbers - ugly but non-fatal, and the admin
  // sees SOMETHING they can trace back to the misconfigured element.
  //
  // Empty string still returns '' via the second guard below.
  if (text == null) return '';
  if (typeof text !== 'string') text = String(text);
  if (text === '') return text;
  if (!values || typeof values !== 'object') return text;
  // Pass 1: swap doubled braces for sentinels so they survive the
  // placeholder pass.
  let step = text.replace(/\{\{/g, LBRACE_SENTINEL)
                 .replace(/\}\}/g, RBRACE_SENTINEL);
  // Pass 2: normal placeholder substitution.
  step = step.replace(PLACEHOLDER, (match, key) => {
    const v = readValue(values, key);
    if (v == null) return '';
    // 0.46.16 - A list shows its items joined by commas.
    if (Array.isArray(v)) return listText(v);
    // 0.38.23 - Delegate to caller's formatter when provided. The
    // formatter handles cascade formatting, opt-out marker
    // stripping, and any other display-time transforms so this
    // helper doesn't need to know about form state or expression
    // context. Fallback stays String(v) for callers that don't
    // pass a formatter.
    if (typeof formatValue === 'function') {
      const formatted = formatValue(v);
      if (formatted != null) return String(formatted);
    }
    return String(v);
  });
  // Pass 3: restore escaped braces as literal { and }.
  return step.split(LBRACE_SENTINEL).join('{')
             .split(RBRACE_SENTINEL).join('}');
}
